import { Grid, InputLabel, MenuItem, Typography } from '@mui/material';
import { Dialog } from '@mui/material';
import { DialogProps } from '@mui/material';
import { DialogContentText } from '@mui/material';
import { memo, useEffect, useMemo } from 'react';
import { PmiDialogContent } from 'shell/components/shared/dialog/content';
import PmiTextField from 'shell/components/shared/text-field';
import { styled } from '@mui/styles';
import PmiDatePicker from 'shell/components/shared/date-picker';
import { PmiButton } from 'shell/components/shared/button';
import { validationSchema } from './constants';
import { PmiFormControl } from 'shell/components/shared/pmi-select/pmi-form-control';
import { PmiSelect } from 'shell/components/shared/pmi-select';
import { ItemVM } from 'shell/view-models/item';
import { useFormik } from 'formik';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material';
import { getFatherDomainNameBasedOnType, getFatherDomainShortDescription } from '../utils';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ValueDomainType } from 'shell/models/value-domain-type';
import { NewHttpResponseList } from 'shell/models/http-response';
import { transformActDomainTypeValues } from 'civil-status/pages/act-domain-management/utils/transform-act-domain-type-values';
import { ActDomainManagementVM } from 'civil-status/view-models/act-domain-managment-list';
import {
  ActValueDomainModalFormVM,
  ActValueDomainResponseVM,
  ActValueDomainVM,
} from 'civil-status/view-models/act-value-domain-managment-list';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { genders } from 'civil-status/components/place/form/gender';
import { ValueDomainModalOpenType } from '../form/constants';
import { ValueDomainModalType } from 'civil-status/view-models/act-value-domain';
import { useUserProfile } from 'shell/hooks/use-user-profile';

interface DomainValueFormModalComponentProps {
  domainModal: ValueDomainModalType;
  onClose: (open?: boolean) => void;
  title: string;
  onSubmit: (values: ActValueDomainModalFormVM) => void;
  initialValues?: ActValueDomainModalFormVM;
  disabled?: boolean;
  actDomainsList?: Array<ActDomainManagementVM>;
}

export const ActDomainModalDialog = styled((props: DialogProps) => <Dialog {...props} />)(
  (props) => ({
    '& .MuiDialog-paper': props.fullScreen
      ? {
          minWidth: 'unset',
          maxWidth: '100%',
        }
      : {
          minWidth: '42vw',
          maxWidth: '60vw',
        },
  })
);

const DomainValueFormModalComponent = ({
  domainModal,
  title,
  onSubmit,
  onClose,
  initialValues,
  disabled,
  actDomainsList = [],
}: DomainValueFormModalComponentProps) => {
  const typeFatherDomains = useMemo(() => {
    const domains = actDomainsList || [];

    const res: Array<ItemVM> =
      (domains || [])?.map((item: ActDomainManagementVM) => {
        return {
          id: item?.domainType || '',
          value: item?.domainType || '',
        };
      }) || [];

    return res;
  }, [actDomainsList]);

  const [valuesDomain, getValuesDomain] = useHttpFetch<
    NewHttpResponseList<ValueDomainType>,
    ActValueDomainResponseVM
  >(`${__ACT_API_URL__}/valori-dominio-atti`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformActDomainTypeValues,
  });

  const valuesDomainList: Array<ActValueDomainVM> = useMemo(() => {
    const list: Array<ActValueDomainVM> =
      (valuesDomain?.data as ActValueDomainResponseVM)?.valuesDomain || [];

    return list;
  }, [valuesDomain?.data]);

  const fatherValuesDomainList: Array<ItemVM> = useMemo(() => {
    return (
      valuesDomainList?.map((item: ActValueDomainVM) => {
        return {
          id: item?.value || '',
          value: item?.value || '',
        };
      }) || []
    );
  }, [valuesDomainList]);

  const { userProfile } = useUserProfile();

  const valueDomainTypeForm = useFormik<ActValueDomainModalFormVM>({
    initialValues: initialValues || {
      entityBody: userProfile.datiLogin?.key?.codiceEnte || '',
      domainType: '',
      domainName: '',
      valueAnsc: '',
      shortDescription: '',
      description: '',
      precedingArticle: '',
      type: '',
      statisticCode: '',
      lawAuthorizeArticle: '',
      startValidityDate: null,
      endValidityDate: null,
      displayOrderNumber: '',
      fatherDomainType: '',
      fatherDomainName: '',
      fatherDomainValue: '',
      fatherDomainShortDescription: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values: ActValueDomainModalFormVM) => {
      onSubmit(values);
      valueDomainTypeForm.resetForm();
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  useEffect(() => {
    if (domainModal.type === ValueDomainModalOpenType.Add) {
      return;
    }

    if (valuesDomainList && valuesDomainList?.length > 0) {
      valueDomainTypeForm.setFieldValue(
        'fatherDomainShortDescription',
        getFatherDomainShortDescription(valuesDomainList, initialValues?.fatherDomainValue)
      );
    }
  }, [
    getFatherDomainShortDescription,
    valuesDomainList,
    initialValues?.fatherDomainValue,
    domainModal.type,
  ]);

  useEffect(() => {
    const getValuesDomainFunc = async () => {
      if (valueDomainTypeForm?.values?.fatherDomainType) {
        await getValuesDomain({
          url: `?filters=idDominio:${valueDomainTypeForm?.values?.fatherDomainType}:EQUALS`,
        });
      }
    };
    getValuesDomainFunc();
  }, [valueDomainTypeForm?.values?.fatherDomainType, getValuesDomain]);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  return (
    <ActDomainModalDialog
      open={domainModal?.open}
      fullScreen={fullScreen}
      onClose={() => onClose(false)}
      aria-labelledby="domain-value-form-modal"
      aria-describedby="domain-value-form-modal"
    >
      <PmiDialogContent
        sx={{
          padding: '24px',
        }}
      >
        <form onSubmit={valueDomainTypeForm.handleSubmit}>
          <DialogContentText id="alert-dialog-content" padding="24px">
            <Typography
              color="secondary.main"
              style={{
                fontSize: '24px',
              }}
            >
              {title}
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Ente"
                  label="Ente "
                  id="entityBody"
                  disabled
                  name="entityBody"
                  fullWidth
                  value={valueDomainTypeForm.values?.entityBody || ''}
                  onChange={valueDomainTypeForm.handleChange}
                  onBlur={valueDomainTypeForm.handleBlur}
                  inputProps={{
                    maxLength: '2',
                  }}
                  error={
                    valueDomainTypeForm.touched.entityBody &&
                    Boolean(valueDomainTypeForm.errors.entityBody)
                  }
                  helperText={valueDomainTypeForm.errors.entityBody}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Tipo dominio"
                  disabled
                  label="Tipo dominio "
                  id="domainType"
                  name="domainType"
                  fullWidth
                  value={valueDomainTypeForm.values?.domainType || ''}
                  onChange={valueDomainTypeForm.handleChange}
                  onBlur={valueDomainTypeForm.handleBlur}
                  error={
                    valueDomainTypeForm.touched.domainType &&
                    Boolean(valueDomainTypeForm.errors.domainType)
                  }
                  helperText={valueDomainTypeForm.errors.domainType}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  aria-label="Nome dominio"
                  label="Nome dominio "
                  disabled
                  id="domainName"
                  name="domainName"
                  fullWidth
                  value={valueDomainTypeForm.values?.domainName || ''}
                  onChange={valueDomainTypeForm.handleChange}
                  onBlur={valueDomainTypeForm.handleBlur}
                  inputProps={{
                    maxLength: '250',
                  }}
                  error={
                    valueDomainTypeForm.touched.domainName &&
                    Boolean(valueDomainTypeForm.errors.domainName)
                  }
                  helperText={valueDomainTypeForm.errors.domainName}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Valore"
                  label="Valore *"
                  id="value"
                  name="value"
                  fullWidth
                  inputProps={{
                    maxLength: '10',
                  }}
                  disabled={disabled || domainModal?.type === ValueDomainModalOpenType.Edit}
                  value={valueDomainTypeForm.values?.value || ''}
                  onChange={valueDomainTypeForm.handleChange}
                  onBlur={valueDomainTypeForm.handleBlur}
                  error={
                    valueDomainTypeForm.touched.value && Boolean(valueDomainTypeForm.errors.value)
                  }
                  helperText={valueDomainTypeForm.errors.value}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Valore ANSC"
                  label="Valore ANSC "
                  id="valueAnsc"
                  disabled={disabled}
                  name="valueAnsc"
                  fullWidth
                  inputProps={{
                    maxLength: '10',
                  }}
                  value={valueDomainTypeForm.values?.valueAnsc || ''}
                  onChange={valueDomainTypeForm.handleChange}
                  onBlur={valueDomainTypeForm.handleBlur}
                  error={
                    valueDomainTypeForm.touched.valueAnsc &&
                    Boolean(valueDomainTypeForm.errors.valueAnsc)
                  }
                  helperText={valueDomainTypeForm.errors.valueAnsc}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <PmiTextField
                  aria-label="Descrizione breve valore"
                  label="Descrizione breve valore *"
                  id="shortDescription"
                  name="shortDescription"
                  inputProps={{
                    maxLength: '100',
                  }}
                  disabled={disabled}
                  fullWidth
                  value={valueDomainTypeForm.values?.shortDescription || ''}
                  onChange={valueDomainTypeForm.handleChange}
                  onBlur={valueDomainTypeForm.handleBlur}
                  error={
                    valueDomainTypeForm.touched.shortDescription &&
                    Boolean(valueDomainTypeForm.errors.shortDescription)
                  }
                  helperText={valueDomainTypeForm.errors.shortDescription}
                />
              </Grid>

              <Grid item xs={12} md={12}>
                <PmiTextField
                  aria-label="Descrizione valore"
                  label="Descrizione valore *"
                  id="description"
                  name="description"
                  fullWidth
                  minRows={5}
                  multiline
                  disabled={disabled}
                  value={valueDomainTypeForm.values?.description || ''}
                  onChange={valueDomainTypeForm.handleChange}
                  onBlur={valueDomainTypeForm.handleBlur}
                  error={
                    valueDomainTypeForm.touched.description &&
                    Boolean(valueDomainTypeForm.errors.description)
                  }
                  helperText={valueDomainTypeForm.errors.description}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <PmiTextField
                  aria-label="Articolo anteposto"
                  label="Articolo anteposto "
                  id="precedingArticle"
                  inputProps={{
                    maxLength: '5',
                  }}
                  name="precedingArticle"
                  fullWidth
                  disabled={disabled}
                  value={valueDomainTypeForm.values?.precedingArticle || ''}
                  onChange={valueDomainTypeForm.handleChange}
                  onBlur={valueDomainTypeForm.handleBlur}
                  error={
                    valueDomainTypeForm.touched.precedingArticle &&
                    Boolean(valueDomainTypeForm.errors.precedingArticle)
                  }
                  helperText={valueDomainTypeForm.errors.precedingArticle}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <PmiFormControl fullWidth>
                  <InputLabel id={'type'}>Genere</InputLabel>
                  <PmiSelect
                    labelId={'type'}
                    id={'type'}
                    name={'type'}
                    error={
                      valueDomainTypeForm.touched.type && Boolean(valueDomainTypeForm.errors.type)
                    }
                    label={'Genere'}
                    disabled={disabled}
                    value={valueDomainTypeForm.values?.type || ''}
                    onChange={(e) => {
                      valueDomainTypeForm.setFieldValue('type', e.target.value);
                    }}
                  >
                    <MenuItem value="">
                      <em>Nessuno</em>
                    </MenuItem>
                    {(genders || [])?.map((gender: ItemVM) => (
                      <MenuItem key={gender?.id} value={gender?.id}>
                        {gender?.value}
                      </MenuItem>
                    ))}
                  </PmiSelect>
                </PmiFormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <PmiTextField
                  aria-label="Cod. statistico"
                  label="Cod. statistico "
                  id="statisticCode"
                  inputProps={{
                    maxLength: '10',
                  }}
                  name="statisticCode"
                  fullWidth
                  disabled={disabled}
                  value={valueDomainTypeForm.values?.statisticCode || ''}
                  onChange={valueDomainTypeForm.handleChange}
                  onBlur={valueDomainTypeForm.handleBlur}
                  error={
                    valueDomainTypeForm.touched.statisticCode &&
                    Boolean(valueDomainTypeForm.errors.statisticCode)
                  }
                  helperText={valueDomainTypeForm.errors.statisticCode}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  aria-label="Autorizz. art. legge"
                  label="Autorizz. art. legge "
                  id="lawAuthorizeArticle"
                  name="lawAuthorizeArticle"
                  inputProps={{
                    maxLength: '250',
                  }}
                  fullWidth
                  disabled={disabled}
                  value={valueDomainTypeForm.values?.lawAuthorizeArticle || ''}
                  onChange={valueDomainTypeForm.handleChange}
                  onBlur={valueDomainTypeForm.handleBlur}
                  error={
                    valueDomainTypeForm.touched.lawAuthorizeArticle &&
                    Boolean(valueDomainTypeForm.errors.lawAuthorizeArticle)
                  }
                  helperText={valueDomainTypeForm.errors.lawAuthorizeArticle}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <PmiDatePicker
                  label="Data inizio validità "
                  aria-label="Data inizio validità"
                  disabled={disabled}
                  value={valueDomainTypeForm.values?.startValidityDate || null}
                  onChange={(date) => {
                    valueDomainTypeForm.setFieldValue('startValidityDate', date);
                  }}
                  renderInput={(params) => (
                    <PmiTextField
                      {...params}
                      id="startValidityDate"
                      name="startValidityDate"
                      fullWidth
                      error={
                        valueDomainTypeForm.touched.startValidityDate &&
                        Boolean(valueDomainTypeForm.errors.startValidityDate)
                      }
                      helperText={valueDomainTypeForm.errors.startValidityDate}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiDatePicker
                  label="Data fine validità "
                  aria-label="Data fine validità"
                  disabled={disabled}
                  value={valueDomainTypeForm.values?.endValidityDate || null}
                  onChange={(date) => {
                    valueDomainTypeForm.setFieldValue('endValidityDate', date);
                  }}
                  renderInput={(params) => (
                    <PmiTextField
                      {...params}
                      id="endValidityDate"
                      name="endValidityDate"
                      fullWidth
                      error={
                        valueDomainTypeForm.touched.endValidityDate &&
                        Boolean(valueDomainTypeForm.errors.endValidityDate)
                      }
                      helperText={valueDomainTypeForm.errors.endValidityDate}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="N. ordinamento"
                  label="N. ordinamento "
                  disabled={disabled}
                  id="displayOrderNumber"
                  name="displayOrderNumber"
                  fullWidth
                  error={
                    valueDomainTypeForm.touched.displayOrderNumber &&
                    Boolean(valueDomainTypeForm.errors.displayOrderNumber)
                  }
                  helperText={valueDomainTypeForm.errors.displayOrderNumber}
                  onChange={valueDomainTypeForm.handleChange}
                  value={valueDomainTypeForm.values?.displayOrderNumber || ''}
                  onBlur={valueDomainTypeForm.handleBlur}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={2}>
                <PmiFormControl fullWidth>
                  <InputLabel id={'fatherDomainType'}>Tipo dominio padre</InputLabel>
                  <PmiSelect
                    labelId={'fatherDomainType'}
                    id={'fatherDomainType'}
                    name={'fatherDomainType'}
                    error={
                      valueDomainTypeForm.touched.fatherDomainType &&
                      Boolean(valueDomainTypeForm.errors.fatherDomainType)
                    }
                    label={'Tipo dominio padre'}
                    disabled={disabled}
                    value={valueDomainTypeForm.values?.fatherDomainType || ''}
                    onChange={(e) => {
                      valueDomainTypeForm.setValues({
                        ...(valueDomainTypeForm?.values || {}),
                        fatherDomainName: e.target?.value
                          ? getFatherDomainNameBasedOnType(actDomainsList, e.target.value)
                          : '',
                        fatherDomainType: e.target?.value || '',
                        fatherDomainValue: '',
                        fatherDomainShortDescription: '',
                      });
                    }}
                  >
                    <MenuItem value="">
                      <em>Nessuno</em>
                    </MenuItem>
                    {(typeFatherDomains || [])?.map((fatherDomain: ItemVM) => (
                      <MenuItem key={fatherDomain?.id} value={fatherDomain?.value}>
                        {fatherDomain?.value}
                      </MenuItem>
                    ))}
                  </PmiSelect>
                </PmiFormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Nome dominio padre"
                  label="Nome dominio padre "
                  id="fatherDomainName"
                  disabled
                  name="fatherDomainName"
                  fullWidth
                  value={valueDomainTypeForm.values?.fatherDomainName || ''}
                  onChange={valueDomainTypeForm.handleChange}
                  onBlur={valueDomainTypeForm.handleBlur}
                  error={
                    valueDomainTypeForm.touched.fatherDomainName &&
                    Boolean(valueDomainTypeForm.errors.fatherDomainName)
                  }
                  helperText={valueDomainTypeForm.errors.fatherDomainName}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiFormControl fullWidth>
                  <InputLabel id={'fatherDomainType'}>Valore dominio padre</InputLabel>
                  <PmiSelect
                    labelId={'fatherDomainValue'}
                    id={'fatherDomainValue'}
                    name={'fatherDomainValue'}
                    inputProps={{
                      maxLength: '10',
                    }}
                    error={
                      valueDomainTypeForm.touched.fatherDomainValue &&
                      Boolean(valueDomainTypeForm.errors.fatherDomainValue)
                    }
                    label={'Valore dominio padre'}
                    disabled={disabled || !valueDomainTypeForm?.values?.fatherDomainType}
                    value={valueDomainTypeForm.values?.fatherDomainValue || ''}
                    onChange={(e) => {
                      valueDomainTypeForm.setValues({
                        ...(valueDomainTypeForm?.values || {}),
                        fatherDomainValue: e.target.value || '',
                        fatherDomainShortDescription: e.target?.value
                          ? getFatherDomainShortDescription(valuesDomainList, e.target.value)
                          : '',
                      });
                    }}
                  >
                    <MenuItem value="">
                      <em>Nessuno</em>
                    </MenuItem>
                    {fatherValuesDomainList?.map((fatherDomain: ItemVM) => (
                      <MenuItem key={fatherDomain?.id} value={fatherDomain?.id}>
                        {fatherDomain?.value}
                      </MenuItem>
                    ))}
                  </PmiSelect>
                </PmiFormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <PmiTextField
                  aria-label="descrizione valore dominio padre"
                  label="descrizione valore dominio padre "
                  id="fatherDomainShortDescription"
                  disabled
                  name="fatherDomainShortDescription"
                  fullWidth
                  value={valueDomainTypeForm.values?.fatherDomainShortDescription || ''}
                  onChange={valueDomainTypeForm.handleChange}
                  onBlur={valueDomainTypeForm.handleBlur}
                  error={
                    valueDomainTypeForm.touched.fatherDomainShortDescription &&
                    Boolean(valueDomainTypeForm.errors.fatherDomainShortDescription)
                  }
                  helperText={valueDomainTypeForm.errors.fatherDomainShortDescription}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <PmiButton
                  variant="outlined"
                  fullWidth
                  disabled={domainModal?.type === ValueDomainModalOpenType.Read}
                  color="cdmBrandThird"
                  type="submit"
                >
                  Conferma
                </PmiButton>
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiButton
                  fullWidth
                  variant="outlined"
                  color="cdmBrand"
                  type="button"
                  onClick={() => onClose(false)}
                >
                  Annulla
                </PmiButton>
              </Grid>
            </Grid>
          </DialogContentText>
        </form>
      </PmiDialogContent>
    </ActDomainModalDialog>
  );
};

DomainValueFormModalComponent.displayName = 'Domain Value Form Modal Component';
const DomainValueFormModal = memo(DomainValueFormModalComponent);
export default DomainValueFormModal;
